import React, { useState, useEffect, useCallback } from 'react';
import { fetchResidentDetails } from '../../services/api';

const ResidentDetailsModal = ({ isOpen, address, onClose }) => {
  const [residentData, setResidentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchResidentData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setResidentData(null);

    try {
      const data = await fetchResidentDetails(address);
      setResidentData(data);
    } catch (err) {
      console.error('Error fetching resident details:', err);
      setError('Unable to fetch resident details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [address]);

  useEffect(() => {
    if (isOpen && address) {
      fetchResidentData();
    }
  }, [isOpen, address, fetchResidentData]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 px-4">
        <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b bg-gradient-to-r from-green-500 to-green-600">
            <div>
              <h2 className="text-xl font-semibold text-white">
                Resident Details
              </h2>
              <p className="text-green-100 text-sm mt-1">
                {address}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-green-200 focus:outline-none"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-8rem)]">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-10">
                <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-green-600 mb-4"></div>
                <p className="text-gray-600">
                  Searching for resident information...
                </p>
                <p className="text-gray-500 text-sm mt-2">
                  This may take a moment as we search public records
                </p>
              </div>
            ) : error ? (
              <div className="text-center py-10">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                  <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-red-500 mb-4">{error}</p>
                <button
                  onClick={fetchResidentData}
                  className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-700 hover:from-green-600 hover:to-green-800 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Try Again
                </button>
              </div>
            ) : residentData ? (
              <div className="space-y-6">
                {/* Current Residents */}
                {residentData.currentResidents && residentData.currentResidents.length > 0 && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
                      <svg className="h-5 w-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Current Residents
                    </h3>
                    <div className="grid gap-4">
                      {residentData.currentResidents.map((resident, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {resident.name && (
                              <div>
                                <span className="font-medium text-gray-700">Name:</span>
                                <p className="text-gray-900">{resident.name}</p>
                              </div>
                            )}
                            {resident.age && (
                              <div>
                                <span className="font-medium text-gray-700">Age:</span>
                                <p className="text-gray-900">{resident.age}</p>
                              </div>
                            )}
                            {resident.phone && (
                              <div>
                                <span className="font-medium text-gray-700">Phone:</span>
                                <p className="text-gray-900">{resident.phone}</p>
                              </div>
                            )}
                            {resident.email && (
                              <div>
                                <span className="font-medium text-gray-700">Email:</span>
                                <p className="text-gray-900">{resident.email}</p>
                              </div>
                            )}
                            {resident.relatives && resident.relatives.length > 0 && (
                              <div className="md:col-span-2">
                                <span className="font-medium text-gray-700">Related Individuals:</span>
                                <p className="text-gray-900">{resident.relatives.join(', ')}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Previous Residents */}
                {residentData.previousResidents && residentData.previousResidents.length > 0 && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
                      <svg className="h-5 w-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Previous Residents
                    </h3>
                    <div className="grid gap-3">
                      {residentData.previousResidents.map((resident, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                            {resident.name && (
                              <div>
                                <span className="font-medium text-gray-700">Name:</span>
                                <p className="text-gray-900">{resident.name}</p>
                              </div>
                            )}
                            {resident.yearsLived && (
                              <div>
                                <span className="font-medium text-gray-700">Years Lived:</span>
                                <p className="text-gray-900">{resident.yearsLived}</p>
                              </div>
                            )}
                            {resident.age && (
                              <div>
                                <span className="font-medium text-gray-700">Age:</span>
                                <p className="text-gray-900">{resident.age}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Property Information */}
                {residentData.propertyInfo && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
                      <svg className="h-5 w-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      Property Information
                    </h3>
                    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {residentData.propertyInfo.propertyType && (
                          <div>
                            <span className="font-medium text-gray-700">Property Type:</span>
                            <p className="text-gray-900">{residentData.propertyInfo.propertyType}</p>
                          </div>
                        )}
                        {residentData.propertyInfo.yearBuilt && (
                          <div>
                            <span className="font-medium text-gray-700">Year Built:</span>
                            <p className="text-gray-900">{residentData.propertyInfo.yearBuilt}</p>
                          </div>
                        )}
                        {residentData.propertyInfo.lotSize && (
                          <div>
                            <span className="font-medium text-gray-700">Lot Size:</span>
                            <p className="text-gray-900">{residentData.propertyInfo.lotSize}</p>
                          </div>
                        )}
                        {residentData.propertyInfo.estimatedValue && (
                          <div>
                            <span className="font-medium text-gray-700">Estimated Value:</span>
                            <p className="text-gray-900">{residentData.propertyInfo.estimatedValue}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Data Source Info */}
                <div className={`border rounded-lg p-3 ${residentData.dataSource === 'Mock Data' ? 'bg-blue-50 border-blue-200' : 'bg-yellow-50 border-yellow-200'}`}>
                  <div className="flex items-center">
                    <svg className={`h-5 w-5 mr-2 ${residentData.dataSource === 'Mock Data' ? 'text-blue-600' : 'text-yellow-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className={`text-sm ${residentData.dataSource === 'Mock Data' ? 'text-blue-800' : 'text-yellow-800'}`}>
                      <p className="font-medium mb-1">
                        Data Source: {residentData.dataSource || 'Public Records'}
                      </p>
                      <p>
                        {residentData.note || 'Information compiled from public records and directory listings. Data accuracy may vary and should be verified independently.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-10">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                  <svg className="h-6 w-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <p className="text-gray-500">No resident information found for this address.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ResidentDetailsModal; 